import { Injectable } from '@angular/core';
import { ToastController } from '@ionic/angular';

interface ToastOptions {
  message: string;
  duration?: number;
  header?: string;
  priority?: number;
}

type ToastItem = { 
  toastOptions: ToastOptions; 
  toast: HTMLIonToastElement; 
};

@Injectable({
  providedIn: 'root',
})
export class SingleToastService {

  private queue: ToastItem[] = [];
  private currentToast: ToastItem | null = null;

  constructor(
    private readonly toastController: ToastController,
  ) { }

  async enqueueToast(options: ToastOptions, persistence = false): Promise<void> {
    let toastOptions: any = {
      message: options.message,
      header: options.header,
      priority: options.priority
    };

    if (persistence) {
      toastOptions.icon = 'alert-circle-outline';
      toastOptions.buttons = [{
        icon: 'close',
        side: 'end',
        role: 'cancel',
      }];
    } else {
      toastOptions.duration = options.duration || 5000;
    }
    
    const toasts = this.currentToast ? [
      this.currentToast.toastOptions,
      ...this.queue.map((t) => t.toastOptions),
    ] : this.queue.map((t) => t.toastOptions);

    const isPresent = toasts.filter((t) => 
      t.priority === toastOptions.priority &&
      t.header === toastOptions.header &&
      t.message === toastOptions.message
    ).length > 0;
    
    if (isPresent) {
      return;
    }
    
    const toast = await this.toastController.create(toastOptions);
    this.queue.push({ toastOptions: options, toast });
    /** The bigger priority number is, the higher it gets in the queue */
    this.queue.sort((a, b) => (b.toastOptions.priority || 0) - (a.toastOptions.priority || 0));
    this.checkQueue();
  }

  private async checkQueue(): Promise<void> {
    if (this.currentToast || this.queue.length === 0) {
      return;
    }
    const toastObj = this.queue.shift();
    if (!toastObj) {
      return;
    }
    this.currentToast = toastObj;

    await toastObj.toast.present();
    if (toastObj.toastOptions.duration) {
      setTimeout(() => toastObj.toast.dismiss(), toastObj.toastOptions.duration);
    }
    
    toastObj.toast.onDidDismiss().then(() => {
      this.currentToast = null;
      this.checkQueue();
    });
  }
}
